import React from "react";
import { Link } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";

const BOX_IMG =
  "https://static.prod-images.emergentagent.com/jobs/063b9062-b1d7-4f16-8d31-e465e039ed51/images/406df7120a6f61d5a26734ee207025f1cc4c38f0ee6cb74ba0fb777e8ec5e47f.png";

export default function Services() {
  return (
    <main data-testid="services-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="vbb-overline">
              <span className="vbb-dot" />
              Services
            </div>
            <h1
              className="font-display uppercase text-5xl sm:text-7xl lg:text-[8rem] leading-[0.92] tracking-tighter mt-6 pl-[0.35em]"
              data-testid="services-title"
            >
              <BoxedTitle size="lg" thick testid="services-title-box">
                What we
              </BoxedTitle>
              <br />
              build, and
              <br />
              <span className="text-[#e34234]">how.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-[17px] leading-relaxed text-[#2a2a2a]">
              Very Big Box is a small business development studio. We take
              ideas from rough sketch to live product — strategy, design,
              engineering, AI integration — and we partner with founders for
              the long stretch.
            </p>
          </div>
          <div
            className="lg:col-span-5 aspect-[4/5] border border-[#0a0a0a] overflow-hidden vbb-grain relative"
            data-testid="services-hero-image"
          >
            <img
              src={BOX_IMG}
              alt="Floating boxes"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 vbb-border-t bg-[#fafafa]">
              <div className="font-mono-tiny text-[#0a0a0a]">
                Fig. 02 — Each project, its own box.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section
        className="vbb-border-t vbb-border-b"
        data-testid="services-grid"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-3">
          {[
            {
              n: "S/01",
              t: "Business Development",
              d: "Market shape-finding, positioning, partnerships, and the un-glamorous decisions that compound. We help founders pick the right fight.",
            },
            {
              n: "S/02",
              t: "Product Incubation",
              d: "We design, prototype, and ship 0→1 products inside VBB. Some we operate. Some we spin out. Some we co-build with founders.",
            },
            {
              n: "S/03",
              t: "AI-Driven Engineering",
              d: "We use modern AI tooling (incl. Emergent.sh) to compress timelines without losing the craft. Speed where it pays, care where it matters.",
            },
          ].map((s, i) => (
            <div
              key={s.n}
              className={`p-10 lg:p-14 ${
                i !== 2 ? "lg:vbb-border-r" : ""
              } ${i !== 0 ? "vbb-border-t lg:!border-t-0" : ""}`}
              data-testid={`service-${s.n}`}
            >
              <div className="font-mono-tiny text-[#6b6b6b]">{s.n}</div>
              <h3 className="font-display text-2xl sm:text-3xl mt-4 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">{s.t}</BoxedTitle>
              </h3>
              <p className="mt-5 text-[15px] leading-relaxed text-[#2a2a2a]">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Emergent promo block - red accent */}
      <section
        className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32"
        data-testid="emergent-promo"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="font-mono-tiny text-[#6b6b6b]">
              <span className="vbb-dot" />
              Partner Tooling
            </div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 uppercase tracking-tighter leading-[0.95] pl-[0.35em]">
              <BoxedTitle size="md">We build with</BoxedTitle>
              <br />
              <span className="text-[#e34234]">Emergent.sh</span>
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-[#2a2a2a] max-w-md">
              Emergent is the AI-native development platform we use to turn an
              idea into a working product in days, not quarters — backend,
              frontend, integrations, deploy, the lot. If you’re a founder
              with a real problem and limited runway, this stack is unfair in
              the best way.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div
              className="relative bg-[#e34234] text-[#fafafa] p-10 lg:p-14 vbb-grain"
              data-testid="emergent-promo-card"
            >
              <div className="font-mono-tiny opacity-80">
                Available now — limited engagements
              </div>
              <h3 className="font-display text-3xl sm:text-5xl mt-4 uppercase tracking-tighter leading-[0.95] pl-[0.35em]">
                <BoxedTitle size="md" boxColor="border-[#fafafa]">
                  We’re available
                </BoxedTitle>{" "}
                to build AI-driven projects with you, on Emergent.
              </h3>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[15px]">
                {[
                  "0→1 product builds",
                  "Internal AI tools",
                  "Founder-mode prototyping",
                  "Production-ready MVPs",
                  "AI integrations (LLM/Vision/Voice)",
                  "Backend + Frontend + Deploy",
                ].map((x) => (
                  <li
                    key={x}
                    className="flex items-start gap-3 border-t border-[#fafafa]/30 pt-3"
                  >
                    <span className="font-mono-tiny mt-1 opacity-80">●</span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="https://emergent.sh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vbb-btn !bg-[#fafafa] !text-[#0a0a0a] !border-[#fafafa] hover:!bg-[#0a0a0a] hover:!text-[#fafafa] hover:!border-[#0a0a0a]"
                  data-testid="emergent-try-btn"
                >
                  Try Emergent.sh &#8599;
                </a>
                <Link
                  to="/contact"
                  className="vbb-btn !bg-transparent !text-[#fafafa] !border-[#fafafa] hover:!bg-[#fafafa] hover:!text-[#e34234] hover:!border-[#fafafa]"
                  data-testid="emergent-collab-btn"
                >
                  Build with us →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process strip */}
      <section
        className="vbb-border-t vbb-border-b bg-[#fafafa]"
        data-testid="services-process"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-4">
          {[
            { n: "01", t: "Listen", d: "Real problem first." },
            { n: "02", t: "Shape", d: "Smallest honest version." },
            { n: "03", t: "Build", d: "Ship in weeks." },
            { n: "04", t: "Operate", d: "Learn, iterate, hand-off." },
          ].map((s, i) => (
            <div
              key={s.n}
              className={`p-8 lg:p-10 ${
                i !== 3 ? "md:vbb-border-r" : ""
              } ${i !== 0 ? "vbb-border-t md:!border-t-0" : ""}`}
              data-testid={`process-${s.n}`}
            >
              <div className="font-mono-tiny text-[#6b6b6b]">{s.n}</div>
              <div className="font-display text-2xl mt-3 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">{s.t}</BoxedTitle>
              </div>
              <div className="mt-2 text-[14px] text-[#3a3a3a]">{s.d}</div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
